import { useEffect, useMemo, useState } from "react";
import { Camera as CameraIcon, Download, RefreshCw, RotateCw, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import DeviceSelector from "components/DeviceSelector";
import CameraDeleteDialog from "components/camera/CameraDeleteDialog";
import CameraGallery from "components/camera/CameraGallery";
import CameraImageModal from "components/camera/CameraImageModal";
import CameraMissingPanel from "components/camera/CameraMissingPanel";
import CameraRunSelector from "components/camera/CameraRunSelector";
import CameraScheduleForm from "components/camera/CameraScheduleForm";
import CameraStatusCard from "components/camera/CameraStatusCard";
import { useCamera } from "hooks/useCamera";
import { useDevices } from "hooks/useDevices";
import { downloadCameraRun } from "services/cameraApi";

export default function CameraPage() {
  const { macAddress } = useParams();
  const navigate = useNavigate();
  const { devices, selectedDevice, setSelectedDevice, loading: devicesLoading } = useDevices();
  const routeMac = macAddress ? decodeURIComponent(macAddress) : "";
  const activeMac = routeMac || selectedDevice;
  const camera = useCamera(activeMac);
  const [openImage, setOpenImage] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionError, setActionError] = useState("");
  const [capturingNow, setCapturingNow] = useState(false);
  const capturePhase = camera.capture.phase;
  const waitingForPhoto = capturePhase === "waiting";

  const captureNow = async () => {
    setActionError("");
    setCapturingNow(true);
    try {
      await camera.requestCaptureNow();
    } catch (error) {
      setActionError(error.message);
    } finally {
      setCapturingNow(false);
    }
  };

  useEffect(() => {
    if (routeMac && routeMac !== selectedDevice) setSelectedDevice(routeMac);
  }, [routeMac, selectedDevice, setSelectedDevice]);

  useEffect(() => {
    if (!routeMac && selectedDevice) {
      navigate(`/devices/${encodeURIComponent(selectedDevice)}/camera`, { replace: true });
    }
  }, [navigate, routeMac, selectedDevice]);

  const selectedRun = useMemo(
    () => camera.runs.find((run) => String(run.id) === String(camera.selectedRunId)),
    [camera.runs, camera.selectedRunId]
  );

  const changeDevice = (mac) => {
    setSelectedDevice(mac);
    if (mac) navigate(`/devices/${encodeURIComponent(mac)}/camera`);
  };

  const exportRun = async () => {
    if (!selectedRun) return;
    setActionError("");
    try {
      await downloadCameraRun(selectedRun);
    } catch (error) {
      setActionError(error.message);
    }
  };

  if (!activeMac && devicesLoading) {
    return <div className="p-6 text-slate-500">Loading authorized devices…</div>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Ground truth</p>
          <h1 className="mt-1 text-3xl font-extrabold text-slate-900">Ruler camera</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            RTC-aligned, durably stored images. "Capture now" gets a response
            within a couple of seconds from a standalone always-on camera (the
            request is pushed to it over MQTT) — a PadiHAT-relayed camera has
            no way to notice it until its next scheduled wake.
          </p>
        </div>
        <div className="w-full lg:w-80">
          <DeviceSelector
            devices={devices}
            value={activeMac || ""}
            onChange={changeDevice}
            placeholder="Select a device"
          />
        </div>
      </header>

      {!activeMac ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          Select an authorized device to view its camera runs.
        </div>
      ) : (
        <>
          {(camera.error || actionError) && (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {camera.error || actionError}
            </div>
          )}

          <CameraStatusCard status={camera.status} />
          <CameraScheduleForm
            currentRun={camera.status?.run}
            currentInterval={camera.status?.configured_interval_seconds}
            onSubmit={camera.createRun}
          />

          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div className="w-full md:max-w-md">
                <CameraRunSelector
                  runs={camera.runs}
                  value={camera.selectedRunId}
                  onChange={camera.selectRun}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={captureNow}
                  disabled={!activeMac || capturingNow || waitingForPhoto}
                  title="Only a standalone camera running its test-mode firmware notices this within a few seconds"
                  className="flex items-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-bold text-blue-700 disabled:opacity-40"
                >
                  <CameraIcon className={`h-4 w-4 ${capturingNow || waitingForPhoto ? "animate-pulse" : ""}`} />
                  {capturingNow ? "Requesting…" : waitingForPhoto ? "Waiting for photo…" : "Capture now"}
                </button>
                <button
                  onClick={() => camera.refresh()}
                  className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-700"
                >
                  <RefreshCw className="h-4 w-4" /> Refresh
                </button>
                <button
                  onClick={() => camera.setImageRotation(camera.status?.image_rotation_deg === 180 ? 0 : 180)}
                  aria-pressed={camera.status?.image_rotation_deg === 180}
                  title="Turn this camera's photos upside down for everyone (display only - the stored photos are unchanged)"
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold ${
                    camera.status?.image_rotation_deg === 180
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  <RotateCw className="h-4 w-4" /> Rotate 180°
                </button>
                <button
                  onClick={exportRun}
                  disabled={!selectedRun}
                  className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-700 disabled:opacity-40"
                >
                  <Download className="h-4 w-4" /> Export ZIP
                </button>
                <button
                  onClick={() => setDeleteOpen(true)}
                  disabled={!selectedRun}
                  className="flex items-center gap-2 rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-bold text-red-700 disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            </div>
            {capturePhase === "arrived" && (
              <p role="status" className="mt-3 text-sm font-semibold text-emerald-700">Photo received.</p>
            )}
            {capturePhase === "failed" && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                The camera could not take the photo: {camera.capture.reason}.
              </p>
            )}
            {capturePhase === "timeout" && (
              <p role="alert" className="mt-3 text-sm text-amber-800">
                No photo after 60 seconds. Check that the camera is online (see the status above) and try again;
                if it was only slow, the photo will still show up here by itself.
              </p>
            )}
          </section>

          {camera.loading ? (
            <div className="rounded-2xl bg-white p-10 text-center text-slate-500">Loading camera records…</div>
          ) : (
            <CameraGallery
              images={camera.images}
              thumbnailUrls={camera.thumbnailUrls}
              onOpen={setOpenImage}
              onLoadMore={camera.loadMore}
              hasMore={camera.hasMore}
              loadingMore={camera.loadingMore}
              pendingCapture={camera.capture}
              highlightImageId={capturePhase === "arrived" ? camera.capture.imageId : null}
            />
          )}
          <CameraMissingPanel rows={camera.missing} />
        </>
      )}

      <CameraImageModal image={openImage} onClose={() => setOpenImage(null)} />
      {deleteOpen && (
        <CameraDeleteDialog
          run={selectedRun}
          onDelete={camera.removeRun}
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </div>
  );
}
